import React, { useCallback, useEffect, useState } from 'react';
import { MdQrCode2, MdDelete, MdCheckCircle } from 'react-icons/md';
import axiosInstance from '../services/axiosInstance';
import { useLanguage } from '../context/LanguageContext';
import { text } from '../text';
import { theme } from '../constants';

// SN installation item yg BENAR-BENAR terpasang di kapal task ini — dipilih dari SN bawaan checker
// (grup perjalanan: bawaan bersama) atau diketik manual. Hanya SN di sini yg nantinya ditautkan
// admin ke data produk kapal; SN rencana di daftar bawaan tidak pernah ditulis langsung.

const QrIcon = MdQrCode2 as any;
const DeleteIcon = MdDelete as any;
const CheckIcon = MdCheckCircle as any;

interface PoolItem { checker_checklist_id: number; title: string; serial_number: string; used_task_id: number | null; used_vessel_name: string | null }
interface Installed { id: number; item_title: string; serial_number: string; source: string; link_status: string; linked_product_name: string | null; installed_by_name: string | null }
interface Data { pool: PoolItem[]; items: { checker_checklist_id: number; title: string }[]; installed: Installed[]; can_record: boolean }

export const InstalledSerialsSection: React.FC<{ taskId: number }> = ({ taskId }) => {
  const { t } = useLanguage();
  const tg = (t as any).tripGroup || {};
  const [data, setData] = useState<Data | null>(null);
  const [manualItem, setManualItem] = useState('');
  const [manualSerial, setManualSerial] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    axiosInstance.get(`/installed-serials/task/${taskId}`).then((r) => setData(r.data)).catch(() => {});
  }, [taskId]);
  useEffect(() => { load(); }, [load]);

  const record = async (checkerChecklistId: number, serial: string) => {
    setBusy(true);
    setError('');
    try {
      await axiosInstance.post(`/installed-serials/task/${taskId}`, { checker_checklist_id: checkerChecklistId, serial_number: serial });
      setManualSerial('');
      load();
    } catch (e: any) {
      const d = e.response?.data;
      setError(d?.code === 'SERIAL_USED'
        ? (tg.serialUsed || '').split('{vessel}').join(d.vessel_name || '-')
        : d?.message || t.common.error);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: number) => {
    setBusy(true);
    try {
      await axiosInstance.delete(`/installed-serials/${id}`);
      load();
    } catch (e: any) {
      setError(e.response?.data?.message || t.common.error);
    } finally {
      setBusy(false);
    }
  };

  // Tampil hanya kalau daftar bawaan punya installation item atau sudah ada SN tercatat
  if (!data || (data.items.length === 0 && data.installed.length === 0)) return null;
  const available = data.pool.filter((p) => !p.used_task_id);
  const usedElsewhere = data.pool.filter((p) => p.used_task_id && Number(p.used_task_id) !== Number(taskId));

  const chip = (bg: string, color: string, label: string) => (
    <span style={{ backgroundColor: bg, color, borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>{label}</span>
  );

  return (
    <div style={{ borderTop: '1px solid #F4F6F8', paddingTop: 10, marginTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <QrIcon size={18} color="#059669" />
        <text.H5>{tg.installedTitle || 'SN Terpasang'}</text.H5>
      </div>
      <text.T12 style={{ color: '#6B7280', display: 'block', marginBottom: 8 }}>{tg.installedHint}</text.T12>
      {error && <text.T12 style={{ color: '#DC2626', display: 'block', marginBottom: 6 }}>{error}</text.T12>}

      {/* SN yg sudah dicatat di task ini */}
      {data.installed.length === 0 ? (
        <text.T12 style={{ color: '#9CA3AF', display: 'block', marginBottom: 8 }}>{tg.noneInstalled}</text.T12>
      ) : data.installed.map((s) => (
        <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid #D1FAE5', backgroundColor: '#F0FDF4', borderRadius: 10, marginBottom: 6 }}>
          <CheckIcon size={16} color="#059669" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <text.T14 style={{ fontWeight: '600', color: '#111827' }}>{s.serial_number}</text.T14>
            <text.T12 style={{ color: '#6B7280', display: 'block' }}>
              {s.item_title}{s.linked_product_name ? ` · ${tg.linkedTo || 'Ditautkan ke'} ${s.linked_product_name}` : ''}
            </text.T12>
          </div>
          {s.link_status === 'linked' ? chip('#D1FAE5', '#065F46', 'Linked')
            : s.link_status === 'skipped' ? chip('#F3F4F6', '#6B7280', 'Skipped')
            : chip('#FEF3C7', '#92400E', 'Pending')}
          {data.can_record && s.link_status === 'pending' && (
            <div onClick={() => !busy && remove(s.id)} style={{ cursor: 'pointer', padding: 2 }}>
              <DeleteIcon size={16} color="#EF4444" />
            </div>
          )}
        </div>
      ))}

      {data.can_record && (
        <>
          {/* Pilih dari SN bawaan checker */}
          <text.T12 style={{ color: '#374151', fontWeight: '600', display: 'block', margin: '8px 0 6px' }}>{tg.pickFromPrepared || 'Dari bawaan checker'}</text.T12>
          {available.length === 0 && usedElsewhere.length === 0 ? (
            <text.T12 style={{ color: '#9CA3AF', display: 'block' }}>{tg.noPrepared}</text.T12>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {available.map((p) => (
                <div key={`${p.checker_checklist_id}-${p.serial_number}`} onClick={() => !busy && record(p.checker_checklist_id, p.serial_number)}
                  style={{ cursor: busy ? 'default' : 'pointer', border: '1px solid #A7F3D0', borderRadius: 8, padding: '5px 10px', backgroundColor: '#fff' }}>
                  <text.T12 style={{ color: '#065F46', fontWeight: '600' }}>+ {p.serial_number}</text.T12>
                  <text.T10 style={{ color: '#6B7280', display: 'block' }}>{p.title}</text.T10>
                </div>
              ))}
              {usedElsewhere.map((p) => (
                <div key={`u-${p.checker_checklist_id}-${p.serial_number}`} style={{ border: '1px solid #E5E7EB', borderRadius: 8, padding: '5px 10px', backgroundColor: '#F9FAFB', opacity: 0.7 }}>
                  <text.T12 style={{ color: '#6B7280', textDecoration: 'line-through' }}>{p.serial_number}</text.T12>
                  <text.T10 style={{ color: '#9CA3AF', display: 'block' }}>{tg.usedOn || 'Terpasang di'} {p.used_vessel_name || '-'}</text.T10>
                </div>
              ))}
            </div>
          )}

          {/* SN lain — terpasang tapi tidak sesuai bawaan */}
          {data.items.length > 0 && (
            <>
              <text.T12 style={{ color: '#374151', fontWeight: '600', display: 'block', margin: '12px 0 6px' }}>{tg.manualEntry || 'SN lain'}</text.T12>
              <div style={{ display: 'flex', gap: 6 }}>
                <select value={manualItem} onChange={(e) => setManualItem(e.target.value)}
                  style={{ flex: 1, minWidth: 0, border: '1px solid #D1D5DB', borderRadius: 8, padding: '8px', fontSize: 13, backgroundColor: '#fff', color: theme.colors.mainDark }}>
                  <option value="">{tg.chooseItem || 'Pilih item'}</option>
                  {data.items.map((i) => <option key={i.checker_checklist_id} value={i.checker_checklist_id}>{i.title}</option>)}
                </select>
                <input value={manualSerial} onChange={(e) => setManualSerial(e.target.value)} placeholder={tg.serialPlaceholder || 'Serial number'}
                  style={{ flex: 1, minWidth: 0, border: '1px solid #D1D5DB', borderRadius: 8, padding: '8px', fontSize: 13, outline: 'none' }} />
                <div onClick={() => !busy && manualItem && manualSerial.trim() && record(Number(manualItem), manualSerial.trim())}
                  style={{ cursor: 'pointer', borderRadius: 8, padding: '8px 12px', backgroundColor: manualItem && manualSerial.trim() ? '#059669' : '#D1D5DB', flexShrink: 0 }}>
                  <text.T12 style={{ color: '#fff', fontWeight: '600' }}>{tg.add || 'Tambah'}</text.T12>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default InstalledSerialsSection;
